<template>
  <div v-if="loading" class="flex items-center gap-1.5 px-2 py-1 text-gray-500 text-xs">
    <span class="animate-pulse">⋯</span>
  </div>

  <div v-else-if="error" class="px-2 py-1 text-red-400 text-xs truncate" :title="error">
    ⚠ {{ error }}
  </div>

  <ul v-else @contextmenu.prevent.stop="onContainerContextMenu">

    <!-- Inline input at root level (creating directly inside props.path) -->
    <li v-if="newItem.visible && newItem.parentDir === path" class="select-none">
      <div class="flex items-center gap-1 px-2 py-0.5">
        <span class="w-3 shrink-0" />
        <span class="shrink-0 text-xs">{{ newItem.type === 'dir' ? '📁' : '📄' }}</span>
        <input
          ref="newItemInputEl"
          v-model="newItem.name"
          class="bg-gray-900 text-gray-100 text-sm border border-blue-500 rounded px-1 w-full outline-none leading-snug"
          @keydown.enter.prevent="confirmNewItem"
          @keydown.esc="cancelNewItem"
          @blur="cancelNewItem"
        />
      </div>
    </li>

    <li v-for="item in items" :key="item.path" class="select-none">

      <!-- ── Directory row ─────────────────────────────────────────── -->
      <template v-if="item.isDir">
        <div
          class="flex items-center gap-1 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-700/60 text-sm"
          @click="toggle(item.path)"
          @contextmenu.prevent.stop="onDirContextMenu($event, item.path)"
        >
          <span class="text-gray-500 text-[10px] w-3 shrink-0">
            {{ expanded[item.path] ? '▾' : '▸' }}
          </span>
          <span class="shrink-0">{{ expanded[item.path] ? '📂' : '📁' }}</span>
          <span class="text-gray-200 truncate">{{ item.name }}</span>
        </div>

        <!-- Inline input below dir row (creating inside this subdirectory) -->
        <div
          v-if="newItem.visible && newItem.parentDir === item.path"
          class="ml-3 border-l border-gray-700/40 pl-1"
        >
          <div class="flex items-center gap-1 px-2 py-0.5">
            <span class="w-3 shrink-0" />
            <span class="shrink-0 text-xs">{{ newItem.type === 'dir' ? '📁' : '📄' }}</span>
            <input
              ref="newItemInputEl"
              v-model="newItem.name"
              class="bg-gray-900 text-gray-100 text-sm border border-blue-500 rounded px-1 w-full outline-none leading-snug"
              @keydown.enter.prevent="confirmNewItem"
              @keydown.esc="cancelNewItem"
              @blur="cancelNewItem"
            />
          </div>
        </div>

        <!--
          Two-phase lazy loading:
            v-if="seen[path]"   — create the component only after first expand
            v-show="expanded[path]" — show/hide on subsequent toggles without re-fetching
          refreshKeys forces a remount when a file/folder was just created inside.
        -->
        <FileTree
          v-if="seen[item.path]"
          v-show="expanded[item.path]"
          :key="`${item.path}:${refreshKeys[item.path] ?? 0}`"
          :path="item.path"
          class="ml-3 border-l border-gray-700/40 pl-1"
        />
      </template>

      <!-- ── File row ──────────────────────────────────────────────── -->
      <template v-else>
        <div
          class="flex items-center gap-1 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-700/60 text-sm"
          :class="activeFile?.path === item.path ? 'bg-blue-600/25 text-white' : 'text-gray-200'"
          @click="openFile(item.path)"
        >
          <span class="w-3 shrink-0" />
          <span class="shrink-0 text-xs">{{ fileIcon(item.name) }}</span>
          <span class="truncate">{{ item.name }}</span>
        </div>
      </template>

    </li>

    <!-- Context menu — inside <ul> so it doesn't create a second template root,
         which would break Vue 3's class attribute inheritance on recursive <FileTree> calls. -->
    <Teleport to="body">
      <div
        v-if="contextMenu.visible"
        class="fixed z-50 bg-gray-800 border border-gray-600 rounded shadow-xl py-0.5 min-w-[150px] text-sm"
        :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
      >
        <button
          class="w-full text-left px-3 py-1.5 hover:bg-gray-700 text-gray-200 flex items-center gap-2"
          @click.stop="startNewItem('file')"
        >
          <span class="text-xs">📄</span> New File
        </button>
        <button
          class="w-full text-left px-3 py-1.5 hover:bg-gray-700 text-gray-200 flex items-center gap-2"
          @click.stop="startNewItem('dir')"
        >
          <span class="text-xs">📁</span> New Folder
        </button>
      </div>
    </Teleport>
  </ul>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { listDirectory, createFile, createFolder, type FileEntry } from '@/services/fileService'
import { useEditorStore } from '@/stores/editorStore'
import { extractMessage } from '@/services/apiClient'

const props = defineProps<{ path: string }>()

const editorStore = useEditorStore()
const activeFile  = computed(() => editorStore.activeFile)

const items   = ref<FileEntry[]>([])
const loading = ref(false)
const error   = ref<string | null>(null)

/**
 * Tracks which directories the user has ever opened.
 * Controls the `v-if` on nested FileTree: once a directory is expanded for the
 * first time, its child component is created and fetches its data. After that,
 * `v-show` (driven by `expanded`) controls visibility so the data is NOT
 * discarded and re-fetched on every collapse/re-expand.
 */
const seen        = ref<Record<string, boolean>>({})
const expanded    = ref<Record<string, boolean>>({})
const refreshKeys = ref<Record<string, number>>({})

const newItemInputEl = ref<HTMLInputElement | null>(null)

const contextMenu = reactive({
  visible:   false,
  x:         0,
  y:         0,
  targetDir: '',
})

const newItem = reactive({
  visible:   false,
  parentDir: '',
  type:      'file' as 'file' | 'dir',
  name:      '',
})

async function reload(): Promise<void> {
  loading.value = true
  error.value   = null
  try {
    items.value = await listDirectory(props.path)
  } catch (err) {
    error.value = extractMessage(err)
  } finally {
    loading.value = false
  }
}

onMounted(reload)

function closeContextMenu(): void {
  contextMenu.visible = false
}

onMounted(()   => { document.addEventListener('click', closeContextMenu) })
onUnmounted(() => { document.removeEventListener('click', closeContextMenu) })

function onDirContextMenu(e: MouseEvent, dirPath: string): void {
  contextMenu.visible   = true
  contextMenu.x         = e.clientX
  contextMenu.y         = e.clientY
  contextMenu.targetDir = dirPath
}

function onContainerContextMenu(e: MouseEvent): void {
  contextMenu.visible   = true
  contextMenu.x         = e.clientX
  contextMenu.y         = e.clientY
  contextMenu.targetDir = props.path
}

function startNewItem(type: 'file' | 'dir'): void {
  const targetDir = contextMenu.targetDir
  contextMenu.visible = false
  if (targetDir !== props.path && !expanded.value[targetDir]) {
    toggle(targetDir)
  }
  newItem.visible   = true
  newItem.parentDir = targetDir
  newItem.type      = type
  newItem.name      = ''
  nextTick(() => newItemInputEl.value?.focus())
}

async function confirmNewItem(): Promise<void> {
  const name      = newItem.name.trim()
  if (!name) { cancelNewItem(); return }
  const fullPath  = newItem.parentDir.replace(/\/$/, '') + '/' + name
  const isDir     = newItem.type === 'dir'
  const parentDir = newItem.parentDir
  cancelNewItem()
  try {
    if (isDir) {
      await createFolder(fullPath)
    } else {
      await createFile(fullPath)
    }
    if (parentDir === props.path) {
      await reload()
    } else {
      refreshKeys.value[parentDir] = (refreshKeys.value[parentDir] ?? 0) + 1
    }
  } catch (err) {
    console.error('[FileTree] create failed:', err)
  }
}

function cancelNewItem(): void {
  newItem.visible = false
  newItem.name    = ''
}

function toggle(dirPath: string): void {
  const next = !expanded.value[dirPath]
  expanded.value[dirPath] = next
  if (next) seen.value[dirPath] = true
}

async function openFile(filePath: string): Promise<void> {
  await editorStore.openFile(filePath)
}

/** Map file extension to a representative emoji icon. */
function fileIcon(name: string): string {
  // split('.').pop() correctly handles dotfiles: '.env' → 'env', '.gitignore' → 'gitignore'
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  const icons: Record<string, string> = {
    php: '🐘',   twig: '🌿',
    ts: '🔷',    tsx: '🔷',    js: '🟨',  jsx: '🟨',  vue: '💚',
    css: '🎨',   less: '🎨',   scss: '🎨',
    json: '📋',  yaml: '📋',   yml: '📋',
    md: '📝',    mdx: '📝',
    env: '🔒',   gitignore: '🔒', htaccess: '🔒',
    html: '🌐',  xml: '🌐',
    sql: '🗃️',   sh: '⚙️',    bash: '⚙️',  lock: '🔒',
  }
  return icons[ext] ?? '📄'
}
</script>
